import { useCallback, useEffect, useState } from 'react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import { ImageStory, TextStory, VideoStory } from './Story'
import type { ImageItem, Item, StoryAspectRatio, StoryVariant, TextItem, VideoItem } from './StoryCarousel.types'
import { aspectRatioVar } from './Story.css'
import {
  fillAnimationDuration,
  imageSequenceProgressBar,
  progressSegment,
  progressSegmentFillFinished,
  progressSegmentFillRunning,
  stepper,
  storyCarousel,
} from './StoryCarouselMobile.css'

const DEFAULT_TIMER_DURATION_IN_SECONDS = 5

export type StoryCarouselProps = {
  items: Array<Item>
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  className?: string
}

export function StoryCarouselMobile({ items, aspectRatio = '3 / 4', storyVariant, className }: StoryCarouselProps) {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [duration, setDuration] = useState(0)

  const stepBack = useCallback(() => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev))
    setDuration(0)
  }, [])

  const stepForward = useCallback(() => {
    // Circle back to the first image when reaching the end
    setActiveIndex((prev) => (prev + 1) % items.length)
    setDuration(0)
  }, [items.length])

  const handleLoadMedia = useCallback(() => {
    setDuration(DEFAULT_TIMER_DURATION_IN_SECONDS)
  }, [])

  const item = items[activeIndex]

  // Automatically step forward every `duration` seconds
  useEffect(() => {
    // We don't want to auto-advance when the duration is unknown (eg. image/video is still loading)
    if (duration > 0) {
      const timeoutId = setTimeout(stepForward, duration * 1000)
      return () => clearTimeout(timeoutId)
    }
  }, [activeIndex, duration, stepForward])

  return (
    <div className={clsx(storyCarousel, className)} style={assignInlineVars({ [aspectRatioVar]: aspectRatio })}>
      <div className={imageSequenceProgressBar}>
        {items.map(({ id }, index) => (
          <ProgressSegment key={id} index={index} activeImageIndex={activeIndex} duration={duration} />
        ))}
      </div>

      <button
        className={stepper}
        data-side="left"
        aria-label={t('STORY_CAROUSEL_PREVIOUS_BUTTON_LABEL')}
        onClick={stepBack}
        disabled={activeIndex === 0}
      />
      {!item.asset && (
        <TextStory
          key={item.id}
          item={item as TextItem}
          aspectRatio={aspectRatio}
          storyVariant={storyVariant}
          onReady={handleLoadMedia}
        />
      )}
      {item.asset?.type === 'image' && (
        <ImageStory
          key={item.id}
          item={item as ImageItem}
          aspectRatio={aspectRatio}
          storyVariant={storyVariant}
          onLoad={handleLoadMedia}
        />
      )}
      {item.asset?.type === 'video' && (
        <VideoStory
          key={item.id}
          item={item as VideoItem}
          aspectRatio={aspectRatio}
          storyVariant={storyVariant}
          autoPlay={true}
          onLoadedMetadata={handleLoadMedia}
        />
      )}
      <button
        className={stepper}
        data-side="right"
        aria-label={t('STORY_CAROUSEL_NEXT_BUTTON_LABEL')}
        onClick={stepForward}
      />
    </div>
  )
}

type ProgressSegmentProps = {
  index: number
  activeImageIndex: number
  duration: number
}

function ProgressSegment({ index, activeImageIndex, duration }: ProgressSegmentProps) {
  let state: 'idle' | 'running' | 'finished' = 'idle'
  if (index === activeImageIndex && duration > 0) {
    state = 'running'
  } else if (index < activeImageIndex) {
    state = 'finished'
  }

  return (
    <div
      style={assignInlineVars({
        [fillAnimationDuration]: `${duration}s`,
      })}
      className={clsx(
        progressSegment,
        state === 'running' && progressSegmentFillRunning,
        state === 'finished' && progressSegmentFillFinished,
      )}
    />
  )
}
